import { config } from '../../config/env';

/**
 * OpenAI API Configuration
 */
export const OPENAI_CONFIG = {
  // Trim the API key to remove any whitespace or hidden characters
  API_KEY: (config.OPENAI_API_KEY || '').trim(),
  API_URL: 'https://api.openai.com/v1/chat/completions',
  MODEL: 'gpt-5-mini',
  TEMPERATURE: 0.3,
  /** Room for output after reasoning (gpt-5 defaults burn budget on reasoning). */
  MAX_TOKENS: 16_384,
} as const;

function buildChatCompletionBody(
  model: string,
  messages: Array<{ role: string; content: string }>,
  temperature: number,
  maxTokens: number
): Record<string, unknown> {
  const body: Record<string, unknown> = { model, messages };

  if (model.startsWith('gpt-5')) {
    // Pre-gpt-5.1 models default to medium reasoning and can return empty content.
    body.max_completion_tokens = maxTokens;
    body.reasoning_effort = 'minimal';
    body.response_format = { type: 'json_object' };
  } else {
    body.temperature = temperature;
    body.max_tokens = maxTokens;
    body.response_format = { type: 'json_object' };
  }

  return body;
}

/**
 * Language name mapping for prompts
 */
export const LANGUAGE_NAMES: Record<string, string> = {
  it: 'Italian',
  en: 'English',
  pt: 'Portuguese',
  fr: 'French',
  es: 'Spanish',
  de: 'German',
} as const;

/**
 * Call OpenAI Chat Completions API using fetch
 */
export const callOpenAI = async (
  messages: Array<{ role: string; content: string }>,
  options?: {
    model?: string;
    temperature?: number;
    maxTokens?: number;
  }
): Promise<{
  content: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}> => {
  // Ensure API key is trimmed
  const apiKey = OPENAI_CONFIG.API_KEY.trim();

  if (!apiKey) {
    throw new Error(
      'OpenAI API key is not configured. Please set VITE_OPENAI_API_KEY in your .env.local file.'
    );
  }

  const model = options?.model || OPENAI_CONFIG.MODEL;
  const response = await fetch(OPENAI_CONFIG.API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify(
      buildChatCompletionBody(
        model,
        messages,
        options?.temperature ?? OPENAI_CONFIG.TEMPERATURE,
        options?.maxTokens || OPENAI_CONFIG.MAX_TOKENS
      )
    ),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `OpenAI API error: ${response.status}`;

    try {
      const errorJson = JSON.parse(errorText);
      if (errorJson.error?.message) {
        errorMessage = `OpenAI API error: ${response.status} - ${errorJson.error.message}`;
      } else {
        errorMessage = `OpenAI API error: ${response.status} - ${errorText}`;
      }
    } catch {
      errorMessage = `OpenAI API error: ${response.status} - ${errorText}`;
    }

    throw new Error(errorMessage);
  }

  const data = await response.json();
  const choice = data.choices?.[0];
  const content = String(choice?.message?.content ?? '').trim();

  if (!content) {
    const finishReason = choice?.finish_reason ?? 'unknown';
    throw new Error(
      `OpenAI returned empty content (finish_reason: ${finishReason}). ` +
        'The model may have used the full token budget on internal reasoning; retry or translate fewer keys at once.'
    );
  }

  return {
    content,
    usage: {
      promptTokens: data.usage?.prompt_tokens || 0,
      completionTokens: data.usage?.completion_tokens || 0,
      totalTokens: data.usage?.total_tokens || 0,
    },
  };
};
